// client/src/utils/structureCache.js
// Итерация 8.6: глобальный кэш структур (Вариант C+).
//
// Зачем:
//   - useStructureInfo с локальным useRef-кэшем → 2 запроса, если 2 компонента.
//   - Здесь: единый Map + inFlight + listeners → 1 запрос на structureId.
//
// Приоритеты:
//   1. Cache hit (60 сек) → отдать из Map.
//   2. In-flight → подождать существующий Promise.
//   3. Новый запрос → fetch, положить в Map, разбудить listeners.
//
// ⚠️ Чистый модуль (без React). useStructureInfo — тонкая обёртка сверху.

const CACHE_TTL_MS = 60_000;

// ============================================================================
// СОСТОЯНИЕ (глобальное)
// ============================================================================

// Map<`${type}:${id}`, { data, timestamp }>
const cache = new Map();

// Map<`${type}:${id}`, Promise> — защита от параллельных запросов
const inFlight = new Map();

// Map<`${type}:${id}`, Set<listener>>
const listeners = new Map();

// ============================================================================
// ПОДПИСКА
// ============================================================================

export function subscribe(key, listener) {
  if (!listeners.has(key)) {
    listeners.set(key, new Set());
  }
  listeners.get(key).add(listener);

  return () => {
    const set = listeners.get(key);
    if (set) {
      set.delete(listener);
      if (set.size === 0) {
        listeners.delete(key);
      }
    }
  };
}

function notify(key, value) {
  const set = listeners.get(key);
  if (!set) return;
  for (const listener of set) {
    try {
      listener(value);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[structureCache] listener error:', e);
    }
  }
}

// ============================================================================
// КЛЮЧ
// ============================================================================

export function buildKey(structureType, structureId) {
  if (!structureType || !structureId) return null;
  return `${String(structureType).toLowerCase()}:${structureId}`;
}

// ============================================================================
// ЧТЕНИЕ
// ============================================================================

/**
 * Возвращает кэшированные данные (или null).
 * @param {string} key
 * @param {boolean} ignoreTTL — если true, не проверять TTL
 */
export function getCached(key, ignoreTTL = false) {
  if (!key) return null;
  const entry = cache.get(key);
  if (!entry) return null;
  if (!ignoreTTL && Date.now() - entry.timestamp > CACHE_TTL_MS) {
    return null;
  }
  return entry.data;
}

// ============================================================================
// ЗАГРУЗКА (главная функция)
// ============================================================================

/**
 * Загружает структуру с глобальным кэшем + защитой от гонок.
 *
 * @param {string} structureType
 * @param {string} structureId
 * @param {Function} fetcher — async () => data
 * @param {boolean} force — игнорировать кэш
 * @returns {Promise<any>} — data (или null при ошибке)
 */
export async function loadStructure(structureType, structureId, fetcher, force = false) {
  const key = buildKey(structureType, structureId);
  if (!key || typeof fetcher !== 'function') return null;

  // 1. Cache hit
  if (!force) {
    const cached = getCached(key);
    if (cached) {
      return cached;
    }
  }

  // 2. In-flight (кто-то уже грузит)
  if (inFlight.has(key)) {
    return inFlight.get(key);
  }

  // 3. Новый запрос
  const promise = (async () => {
    try {
      const raw = await fetcher();
      const data = raw?.data || raw;

      // Сохраняем в кэш
      cache.set(key, { data, timestamp: Date.now() });

      // Разбудить подписчиков
      notify(key, data);

      return data;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[structureCache] fetch failed ${key}:`, err?.message || err);
      notify(key, null);
      return null;
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, promise);
  return promise;
}

// ============================================================================
// ИНВАЛИДАЦИЯ
// ============================================================================

export function invalidate(key) {
  if (!key) {
    cache.clear();
    return;
  }
  cache.delete(key);
}

export function invalidateAll() {
  cache.clear();
}

// ============================================================================
// СТАТИСТИКА (для отладки)
// ============================================================================

export function getCacheStats() {
  return {
    size: cache.size,
    inFlight: inFlight.size,
    listeners: Array.from(listeners.entries()).map(([k, set]) => [k, set.size]),
  };
}
